import React, { useState, useRef, useEffect } from 'react';
import JSONEditor from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';
import { saveAs } from 'file-saver';
import axios from 'axios';

const GenerateReport = () => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState({});
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const [generateStatusMessage, setGenerateStatusMessage] = useState('');
  const [saveStatusMessage, setSaveStatusMessage] = useState('');

  // Fetch organizations on mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/organizations', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        // Map all fields, including ReportLink
        const orgs = response.data.map(org => ({
          id: org.OrganizationID?.toString(),
          name: org.Name,
          reportLink: org.ReportLink,
        }));
        setOrganizations(orgs);
        if (orgs.length > 0) {
          setSelectedOrg(orgs[0].id);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error.response?.data?.error);
      }
    };
    fetchOrganizations();
  }, []);

  // Fetch report when org changes
  const fetchReport = React.useCallback(() => {
    if (!selectedOrg) return;
    setLoading(true);
    setGenerateStatusMessage('');
    fetch(`https://genz-staging.feeltiptop.com/api/user/get_report?p=${selectedOrg}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setReportData(data);
        setEditedData(data);
      })
      .catch((error) => {
        setReportData({});
        setEditedData({});
        setGenerateStatusMessage('❌ Error loading report.');
      })
      .finally(() => setLoading(false));
  }, [selectedOrg]);

  useEffect(() => {
    fetchReport();
  }, [selectedOrg, fetchReport]);

  useEffect(() => {
    if (containerRef.current && reportData) {
      if (editorRef.current) {
        editorRef.current.destroy();
      }
      editorRef.current = new JSONEditor(containerRef.current, {
        mode: 'tree',
        mainMenuBar: true,
      });
      editorRef.current.set(reportData);
    }
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [reportData]);

  const handleSaveChanges = () => {
    if (editorRef.current) {
      try {
        const updatedData = editorRef.current.get();
        console.log('Data from editor:', updatedData); // Debug log
        setEditedData(updatedData);
        setReportData(updatedData);
      } catch (e) {
        console.error('Error saving changes:', e);
      }
    }
  };

  const handleCopy = () => {
    if (editedData) {
      navigator.clipboard.writeText(JSON.stringify(editedData, null, 2));
      alert('JSON copied to clipboard!');
    }
  };

  const handleDownload = () => {
    if (editedData) {
      const blob = new Blob([JSON.stringify(editedData, null, 2)], { type: 'application/json' });
      saveAs(blob, 'customized_report.json');
    }
  };

  const handleUpdateReport = async () => {
    if (editedData) {
      try {
        setSaveStatusMessage('💾 Saving report...');
        
        // Check if editedData already has the Report wrapper or if it's the inner data
        let reportStructure;
        if (editedData.Report) {
          // editedData already has the Report wrapper, use it as-is
          reportStructure = {
            ...editedData,
            Report: {
              ...editedData.Report,
              GeneratedAt: new Date().toISOString()
            }
          };
        } else {
          // editedData is the inner data, wrap it properly
          reportStructure = {
            Report: {
              GeneratedAt: new Date().toISOString(),
              DateRange: editedData.DateRange || {
                StartDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                EndDate: new Date().toISOString().split('T')[0],
                DaysIncluded: 30,
                Month: null
              },
              Summary: editedData.Summary || {},
              Comments: editedData.Comments || {},
              Breakdown: editedData.Breakdown || {}
            }
          };
        }

        // Send the data to save as a new report file
        const payload = {
          orgId: selectedOrg,
          report: reportStructure
        };

        const response = await fetch('https://genz-staging.feeltiptop.com/api/user/save_report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setSaveStatusMessage(`❌ Failed to save report: ${errorData.error || 'Unknown error'}`);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setSaveStatusMessage('✅ Report updated successfully!');
        const data = await response.json();
        console.log('Report saved:', data);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSaveStatusMessage(''), 3000);
        
      } catch (e) {
        setSaveStatusMessage('❌ Error saving report.');
        console.error('Error updating report:', e);
      }
    }
  };
  
  // Helper to get the selected org name
  const selectedOrgObj = organizations.find(org => String(org.id) === String(selectedOrg));
  const selectedOrgName = selectedOrgObj ? selectedOrgObj.name : "Default Organization";
  const selectedOrgReportLink = selectedOrgObj ? selectedOrgObj.reportLink : null;

  return (
    <div className="w-full min-h-screen bg-gray-100 flex flex-col items-center py-10">
      <div className="w-full p-8">
        <h1 className="text-2xl font-bold mb-6 text-gray-900">Social Glass AI Report</h1>
        <div className="mb-4 flex flex-col md:flex-row md:items-center gap-4">
          <label className="font-semibold text-gray-700">
            Select Organization:&nbsp;
            <select
              value={selectedOrg}
              onChange={e => setSelectedOrg(e.target.value)}
              className="px-3 py-2 rounded border border-gray-300"
            >
              {organizations.length > 0 ? (
                organizations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))
              ) : (
                <option value="1">Default Organization</option>
              )}
            </select>
          </label>
        </div>
        <div className="mb-2 text-lg font-semibold text-gray-700">
          Viewing report for: <span className="text-blue-700">{selectedOrgName}</span>
        </div>
        {generateStatusMessage && (
          <div
            className="mb-2 text-center font-semibold"
            style={{
              color: generateStatusMessage.startsWith('✅')
                ? 'green'
                : generateStatusMessage.startsWith('⚠️')
                ? 'orange'
                : 'red',
            }}
          >
            {generateStatusMessage}
            {generateStatusMessage.startsWith('✅') && selectedOrgReportLink && (
              <a
                href={selectedOrgReportLink}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 underline text-blue-700"
              >
                Go to Report Page
              </a>
            )}
          </div>
        )}
        {reportData && Object.keys(reportData).length > 0 && (
          <>
            <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <span className="text-gray-700 text-left">
                Edit the report below. <b>Make sure to save the changes you make, otherwise the JSON will not update.</b>
                To reflect the changes in the report, click on "Update Report" after saving.
              </span>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleSaveChanges}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm font-normal"
                >
                  Save Changes
                </button>
                <button
                  onClick={handleUpdateReport}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition text-sm font-normal"
                  disabled={saveStatusMessage.includes('💾')}
                >
                  {saveStatusMessage.includes('💾') ? 'Saving...' : 'Update Report'}
                </button>
              </div>
            </div>
            {saveStatusMessage && (
              <div className="mb-2 text-center font-semibold" style={{ color: saveStatusMessage.startsWith('✅') ? 'green' : saveStatusMessage.startsWith('💾') ? 'blue' : 'red' }}>
                {saveStatusMessage}
              </div>
            )}
            <div className="mt-4" style={{ height: 600 }}>
              <div ref={containerRef} style={{ height: '100%', minHeight: 400 }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GenerateReport;